import React, { useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { slotCounts } from './dayDetailMath';

const fmt = (v, digits = 4) => (v == null || !Number.isFinite(v) ? '—' : Number(v).toFixed(digits));

export default function DayAggregateFooter({ matrixJson, scoreRow, horizonOffset }) {
  const counts = useMemo(() => slotCounts(matrixJson), [matrixJson]);

  const persistedValue = scoreRow && typeof scoreRow.value === 'number' ? scoreRow.value : null;
  const isPenalty = !!scoreRow?.is_penalty;

  const coveragePct = counts.total > 0 ? Math.round((counts.filled / counts.total) * 100) : 0;
  const coverageCaption = counts.fullyCovered
    ? 'fully covered'
    : `${counts.missing} missing slot${counts.missing === 1 ? '' : 's'}`;

  // Coverage-card tooltip text varies by horizon since the slot model differs
  // (intraday: 24 slots per timestamp; D+N: 1 slot per timestamp).
  const isIntraday = horizonOffset === 0;
  const coverageTooltip = isIntraday
    ? "Total slot count = timestamps × 24. Each cell of the matrix below is one slot. A slot counts as 'filled' when it has a fill source (live submission, intraday forward-fill, or cross-horizon substitute). Full coverage (filled = total) is required for the day to qualify."
    : "Total slot count = number of target timestamps. D+N has one slot per timestamp, populated by the most recent prior submission of this horizon whose window covers the timestamp. A timestamp without an eligible submission counts as missing and the day fails qualification.";

  return (
    <div className="flex items-center gap-4 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870/40 px-4 py-3">
      {/* Target Day RMSE */}
      <div className="flex-shrink-0">
        <Label text="Target Day RMSE" tooltip="Authoritative day score the pipeline wrote to MarketForecasterDailyScores. Counts toward the leaderboard, league qualification, and payments. When the day fails qualification, this is replaced by the peer-Q75 penalty value." />
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-2xl font-semibold font-mono tabular-nums text-gray-900 dark:text-gray-100">{fmt(persistedValue)}</span>
          {isPenalty && (
            <span className="rounded bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              Q75 penalty
            </span>
          )}
        </div>
      </div>

      <div className="h-10 w-px flex-shrink-0 border border-gray-300 dark:border-gray-600" />

      {/* Coverage with a slim progress bar */}
      <div className="min-w-0 flex-1">
        <Label text="Coverage" tooltip={coverageTooltip} />
        <div className="mt-0.5 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold font-mono tabular-nums text-gray-900 dark:text-gray-100">
            {counts.filled} / {counts.total}
          </span>
          <span className={`text-xs font-medium font-mono tabular-nums ${counts.fullyCovered ? 'text-brand-600 dark:text-brand-400' : 'text-amber-700 dark:text-amber-300'}`}>
            {coveragePct}% · {coverageCaption}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full border border-gray-300 dark:border-gray-600">
          <div
            className={`h-full rounded-full ${counts.fullyCovered ? 'bg-brand-500' : 'bg-amber-500'}`}
            style={{ width: `${coveragePct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function Label({ text, tooltip }) {
  return (
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
      <span>{text}</span>
      {tooltip && (
        <span
          className="cursor-help text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          title={tooltip}
          aria-label={`${text} info`}
        >
          <InformationCircleIcon className="h-3 w-3" />
        </span>
      )}
    </div>
  );
}
